import { Code, ConnectError } from "@connectrpc/connect";
import { sessionSnapshot, subscribeSession } from "./session";

export type ResourceStatus = "idle" | "loading" | "live" | "reconnecting" | "error";
export function createResource<T, E>(
  initial: () => T,
  open: (signal: AbortSignal) => AsyncIterable<E>,
  reduce: (value: T, event: E) => T,
  options: { idleMs?: number; onExpire?: () => void } = {}
) {
  let value = initial();
  let loaded = false;
  let status: ResourceStatus = "idle";
  let error: string | null = null;
  let version = 0;
  let refs = 0;
  let attempt = 0;
  let controller: AbortController | undefined;
  let retry: ReturnType<typeof setTimeout> | undefined;
  let grace: ReturnType<typeof setTimeout> | undefined;
  let expiry: ReturnType<typeof setTimeout> | undefined;
  let identity = sessionSnapshot().generation;
  let token = sessionSnapshot().tokens?.access_token;
  const listeners = new Set<() => void>();
  const emit = () => {
    version++;
    listeners.forEach((fn) => fn());
  };
  function stop() {
    clearTimeout(retry);
    controller?.abort();
    controller = undefined;
  }
  function scheduleExpiry() {
    clearTimeout(expiry);
    if (refs || listeners.size || !options.onExpire) return;
    expiry = setTimeout(() => {
      destroy();
      options.onExpire?.();
    }, options.idleMs ?? 300000);
  }
  function destroy() {
    refs = 0;
    clearTimeout(grace);
    clearTimeout(expiry);
    stop();
    unsubscribeSession();
    value = initial();
    loaded = false;
    status = "idle";
    error = null;
    listeners.clear();
  }
  function run() {
    if (!refs || controller || sessionSnapshot().status !== "authenticated") return;
    const current = new AbortController();
    controller = current;
    status = attempt ? "reconnecting" : "loading";
    emit();
    void (async () => {
      try {
        for await (const event of open(current.signal)) {
          if (current.signal.aborted || controller !== current) return;
          value = reduce(value, event);
          loaded = true;
          status = "live";
          error = null;
          attempt = 0;
          emit();
        }
        throw new ConnectError("Subscription ended", Code.Unavailable);
      } catch (cause) {
        if (current.signal.aborted || controller !== current) return;
        controller = undefined;
        const failure = ConnectError.from(cause);
        error = failure.rawMessage;
        const terminal = [
          Code.PermissionDenied,
          Code.InvalidArgument,
          Code.NotFound,
          Code.Unimplemented,
          Code.Unauthenticated,
        ].includes(failure.code);
        status = terminal ? "error" : "reconnecting";
        emit();
        if (!terminal && refs)
          retry = setTimeout(
            run,
            Math.min(1000 * 2 ** attempt++, 30000) * (0.8 + Math.random() * 0.4)
          );
      }
    })();
  }
  const unsubscribeSession = subscribeSession(() => {
    const next = sessionSnapshot();
    if (identity !== next.generation) {
      identity = next.generation;
      stop();
      value = initial();
      loaded = false;
      status = "idle";
      error = null;
      emit();
    }
    if (token !== next.tokens?.access_token) {
      token = next.tokens?.access_token;
      stop();
      attempt = 0;
    }
    if (next.status !== "authenticated") stop();
    else run();
  });
  // A resource created by an abandoned render must also expire.
  scheduleExpiry();
  return {
    get: () => value,
    loaded: () => loaded,
    status: () => status,
    error: () => error,
    version: () => version,
    observe(fn: () => void) {
      listeners.add(fn);
      clearTimeout(expiry);
      return () => {
        listeners.delete(fn);
        scheduleExpiry();
      };
    },
    acquire() {
      refs++;
      clearTimeout(expiry);
      clearTimeout(grace);
      run();
      let released = false;
      return () => {
        if (released) return;
        released = true;
        refs = Math.max(0, refs - 1);
        scheduleExpiry();
        if (!refs)
          grace = setTimeout(() => {
            stop();
            status = "idle";
            emit();
          }, 1000);
      };
    },
    update(fn: (previous: T) => T) {
      value = fn(value);
      emit();
    },
    refresh() {
      stop();
      attempt = 0;
      run();
    },
    destroy,
  };
}
